import React, { createContext, useContext, useEffect, useRef } from 'react';
import { useAppContext } from './util/app-provider/context';

const HotkeyContext = createContext();

const shouldIgnoreGlobalHotkey = (e, options) => {
  if (Boolean(options.allowHotkeysInInputs)) return false;
  const tag = e.target.tagName.toLowerCase();
  return ['input', 'textarea', 'select'].includes(tag) || e.target.isContentEditable;
};

export function HotkeyProvider({ children }) {
  const hotkeysRef = useRef(new Map());
  const { appRef } = useAppContext();

  const registerHotkeys = hotkeyDefs => {
    hotkeyDefs.forEach(({ key, callback, options = {} }) => {
      hotkeysRef.current.set(key.toLowerCase(), { callback, options });
    });
  };

  const unregisterHotkeys = hotkeyDefs => {
    hotkeyDefs.forEach(({ key }) => {
      hotkeysRef.current.delete(key.toLowerCase());
    });
  };

  useEffect(() => {
    const handleKeyDown = e => {
      const hotkeyData = hotkeysRef.current.get(e.key.toLowerCase());
      if (hotkeyData) {
        const { callback, options } = hotkeyData;
        if (options?.appRefsScope && !options?.appRefsScope.includes(appRef)) return;
        if (shouldIgnoreGlobalHotkey(e, options)) return;

        const tag = e.target.tagName.toLowerCase();
        const isInputElement = ['input', 'textarea', 'select'].includes(tag) || e.target.isContentEditable;
        if (!isInputElement || !Boolean(options.allowHotkeysInInputs)) {
          e.preventDefault();
        }

        callback(e);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [appRef]);

  return <HotkeyContext.Provider value={{ registerHotkeys, unregisterHotkeys }}>{children}</HotkeyContext.Provider>;
}

export function useHotkey(hotkeyDefs = []) {
  const { registerHotkeys, unregisterHotkeys } = useContext(HotkeyContext);

  useEffect(() => {
    registerHotkeys(hotkeyDefs);
    return () => unregisterHotkeys(hotkeyDefs);
  }, [hotkeyDefs, registerHotkeys, unregisterHotkeys]);
}
